import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Box } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useInsuranceApi } from '../../../../../hooks/use-insurance-api';
import { downloadFile } from '../../../../../utils/file/download-file';
import { ButtonArea } from '../../../../shared/button-area/button-area';
import { CloseButton, DialogSubTitle, DialogToolbar } from '../../../../shared/dialog/dialog.styles';
import { VerifiedInsuranceWarning } from '../../../shared/components/verified-insurance-warning/verified-insurance-warning';
import { InsuranceVerifiedStatus, StatusText, StatusWrapper } from '../../../shared/styles';
import { withApptRequestIvStatusDialogProvider } from '../../hooks/use-appt-request-iv-status-dialog/hoc';
import { useApptRequestIvStatusDialog } from '../../hooks/use-appt-request-iv-status-dialog/hook';
import { IvInfo } from '../../shared/iv-info/iv-info';
import { ApptRequestIvStatusDialog } from '../appt-request-iv-status-dialog';
import { ManuallyVerifiedButton } from '../shared/manually-verified-button';
import { SelfPayButton } from '../shared/self-pay-button';
import { BoldText } from '../styles';

const InsuranceVerifiedDialog: FC = () => {
  const { status, getVerificationDocumentUrl } = useInsuranceApi();
  const { opened, onClose, isLoading, locationId, appointmentRequest, ivSummary, getCarrierSummary } =
    useApptRequestIvStatusDialog();
  const [verificationPdfUrl, setVerificationPdfUrl] = useState<string>();
  const [carrierSummary] = useState(getCarrierSummary(ivSummary?.idCarrierName));

  useEffect(() => {
    if (!opened || !ivSummary) return;

    (async () => {
      const docUrl = await getVerificationDocumentUrl(locationId, ivSummary.idVerificationRequest);

      setVerificationPdfUrl(docUrl ?? undefined);
    })();
  }, [opened, locationId, ivSummary]);

  const downloadVerificationPdf = async () => {
    if (!verificationPdfUrl) return;

    downloadFile(verificationPdfUrl, 'Verification.pdf');
  };

  return (
    <ApptRequestIvStatusDialog
      opened={opened}
      title={`${appointmentRequest?.patient?.firstName} ${appointmentRequest?.patient?.lastName}`}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <IvInfo />
        <StatusWrapper sx={{ marginTop: '14px', alignItems: 'center' }}>
          <InsuranceVerifiedStatus />
          <StatusText sx={{ marginLeft: '5px' }}>Insurance Verified</StatusText>
        </StatusWrapper>
      </Box>

      <DialogSubTitle sx={{ margin: '30px 0 20px 0', display: 'block' }}>
        The patient's insurance has been <BoldText>successfully verified.</BoldText> You can download the available
        verification form below.
      </DialogSubTitle>

      <ButtonArea
        isLoading={isLoading || status.getVerificationDocumentUrl.isRunning}
        buttons={[
          {
            title: 'Download Verification PDF',
            onClick: downloadVerificationPdf,
            disabled: !verificationPdfUrl,
            endIcon: <DownloadOutlinedIcon />
          }
        ]}
      />

      <VerifiedInsuranceWarning
        locationId={locationId}
        carrierNameId={ivSummary?.idCarrierName}
        rwErrorCode={ivSummary?.rwErrorCode}
        websiteErrorCode={ivSummary?.websiteErrorCode}
        connectionStatus={carrierSummary?.connectionStatus}
        ediErrorMessage={ivSummary?.ediErrorMessage}
      />

      <DialogToolbar>
        <ManuallyVerifiedButton onSuccess={async () => onClose()} disabled={isLoading} />
        <SelfPayButton onSuccess={async () => onClose()} disabled={isLoading} />
        <CloseButton onClick={onClose} disabled={isLoading} />
      </DialogToolbar>
    </ApptRequestIvStatusDialog>
  );
};

export default withApptRequestIvStatusDialogProvider(InsuranceVerifiedDialog);
